import React, { useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Grid,
    GridItem,
    Text,
    Box,
    InputGroup,
    InputLeftAddon,
    VStack,
    Flex,
} from "@chakra-ui/react";

const CompleteProfileModal = ({ isOpen, onClose }) => {
    // Set India as the default country
    const [country, setCountry] = useState("India");
    const [username, setUsername] = useState("");
    const [mobile, setMobile] = useState("");

    const countryDialCodes = {
        India: "+91",
        Nepal: "+977",
        "Sri Lanka": "+94",
        Afghanistan: "+93",
        Russia: "+7",
    };

    const handleUsernameChange = (e) => {
        const value = e.target.value;
        // Only allow alphabetic characters
        if (/^[a-zA-Z\s]*$/.test(value)) {
            setUsername(value);
        }
    };

    const handleMobileChange = (e) => {
        const value = e.target.value;
        // Only allow numeric characters
        if (/^[0-9]*$/.test(value)) {
            setMobile(value);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "xl" }} isCentered scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(5px)" />
            <ModalContent
                borderRadius={{ base: "0", md: "2xl" }}
                maxW={{ base: "100%", md: "700px" }}
                overflow="hidden"
                bg="#f8f9fa"
            >
                {/* Custom Header Section */}
                <Box
                    bgGradient="linear(to-r, #0a1d4a, #1a365d)"
                    p={{ base: 6, md: 8 }}
                    position="relative"
                >
                    <ModalCloseButton color="white" size="lg" top="20px" right="20px" _hover={{ bg: "whiteAlpha.200" }} />
                    <Flex align="center" gap={4} mt={2}>
                        <Flex
                            align="center"
                            justify="center"
                            w="60px"
                            h="60px"
                            bg="whiteAlpha.200"
                            borderRadius="2xl"
                            display={{ base: "none", sm: "flex" }}
                            fontSize="30px"
                        >
                            👤
                        </Flex>
                        <Box>
                            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="800" color="white" letterSpacing="tight" lineHeight="1.2">
                                Complete Profile
                            </Text>
                            <Text color="blue.100" fontSize={{ base: "sm", md: "md" }} mt={1} fontWeight="medium" opacity={0.9}>
                                Setup your account for seamless betting
                            </Text>
                        </Box>
                    </Flex>
                </Box>

                <ModalBody p={{ base: 0, md: 6 }} bg="#f8f9fa">
                    <VStack spacing={6} align="stretch" p={{ base: 4, md: 0 }}>
                        {/* Form Card */}
                        <Box
                            bg="white"
                            p={{ base: 5, md: 8 }}
                            borderRadius={{ base: "xl", md: "2xl" }}
                            boxShadow="lg"
                            border="1px solid"
                            borderColor="gray.100"
                        >
                            <VStack spacing={6}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                        Username
                                    </FormLabel>
                                    <Input
                                        placeholder="Enter your username"
                                        h="50px"
                                        bg="gray.50"
                                        borderRadius="xl"
                                        borderColor="gray.200"
                                        value={username}
                                        onChange={handleUsernameChange}
                                        _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                        _hover={{ borderColor: "gray.300" }}
                                    />
                                </FormControl>

                                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="100%">
                                    <GridItem>
                                        <FormControl isRequired>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                Country
                                            </FormLabel>
                                            <Select
                                                h="50px"
                                                bg="gray.50"
                                                borderRadius="xl"
                                                borderColor="gray.200"
                                                value={country}
                                                onChange={(e) => setCountry(e.target.value)}
                                                _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                            >
                                                <option value="India">India</option>
                                                <option value="Nepal">Nepal</option>
                                                <option value="Sri Lanka">Sri Lanka</option>
                                                <option value="Afghanistan">Afghanistan</option>
                                                <option value="Russia">Russia</option>
                                            </Select>
                                        </FormControl>
                                    </GridItem>
                                    <GridItem>
                                        <FormControl isRequired>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                Mobile
                                            </FormLabel>
                                            <InputGroup h="50px">
                                                <InputLeftAddon
                                                    px={4}
                                                    h="50px"
                                                    bg="gray.100"
                                                    borderColor="gray.200"
                                                    fontWeight="600"
                                                    color="gray.600"
                                                    borderRadius="xl 0 0 xl"
                                                >
                                                    {countryDialCodes[country]}
                                                </InputLeftAddon>
                                                <Input
                                                    type="tel"
                                                    placeholder="Mobile number"
                                                    h="50px"
                                                    bg="gray.50"
                                                    borderRadius="0 xl xl 0"
                                                    borderColor="gray.200"
                                                    borderLeft="none"
                                                    value={mobile}
                                                    onChange={handleMobileChange}
                                                    _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                                />
                                            </InputGroup>
                                        </FormControl>
                                    </GridItem>
                                </Grid>

                                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="100%">
                                    <GridItem>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                Address
                                            </FormLabel>
                                            <Input
                                                placeholder="Street Address"
                                                h="50px"
                                                bg="gray.50"
                                                borderRadius="xl"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                            />
                                        </FormControl>
                                    </GridItem>
                                    <GridItem>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                State
                                            </FormLabel>
                                            <Input
                                                placeholder="State / Province"
                                                h="50px"
                                                bg="gray.50"
                                                borderRadius="xl"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                            />
                                        </FormControl>
                                    </GridItem>
                                </Grid>

                                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} w="100%">
                                    <GridItem>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                Zip Code
                                            </FormLabel>
                                            <Input
                                                placeholder="Postal Code"
                                                h="50px"
                                                bg="gray.50"
                                                borderRadius="xl"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                            />
                                        </FormControl>
                                    </GridItem>
                                    <GridItem>
                                        <FormControl>
                                            <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                                                City
                                            </FormLabel>
                                            <Input
                                                placeholder="City name"
                                                h="50px"
                                                bg="gray.50"
                                                borderRadius="xl"
                                                borderColor="gray.200"
                                                _focus={{ borderColor: "blue.500", bg: "white", boxShadow: "0 0 0 1px #3182ce" }}
                                            />
                                        </FormControl>
                                    </GridItem>
                                </Grid>
                            </VStack>
                        </Box>

                        <Button
                            w="100%"
                            h="56px"
                            bgGradient="linear(to-r, #5c7cfa, #4263eb)"
                            color="white"
                            fontSize="lg"
                            fontWeight="bold"
                            borderRadius="xl"
                            _hover={{
                                bgGradient: "linear(to-r, #4c6ef5, #3b5bdb)",
                                boxShadow: "lg",
                                transform: "translateY(-1px)"
                            }}
                            _active={{ transform: "translateY(0)" }}
                            onClick={onClose}
                            boxShadow="md"
                            mb={4} // Margin bottom for safe area on mobile
                        >
                            Save & Update Profile
                        </Button>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CompleteProfileModal;
